import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import ClaimsList from './pages/ClaimsList';
import NewClaim from './pages/NewClaim';
import ClaimDetail from './pages/ClaimDetail';
import ClientsManagement from './pages/ClientsManagement';
import UsersManagement from './pages/UsersManagement';
import ThirdPartyPortal from './pages/portals/ThirdPartyPortal';
import GaragePortal from './pages/portals/GaragePortal';
import SignatureCallback from './pages/portals/SignatureCallback';
import DriverDeclare from './pages/DriverDeclare';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public: Driver declaration (PWA landing) */}
      <Route path="/" element={<DriverDeclare />} />
      
      {/* Gestionnaire login (desktop only) */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Portal Routes (no auth required) */}
      <Route path="/portal/third-party/:token" element={<ThirdPartyPortal />} />
      <Route path="/portal/third-party/:token/signature-complete" element={<SignatureCallback />} />
      <Route path="/portal/garage/:token" element={<GaragePortal />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Navigate to="/claims" replace />
        </ProtectedRoute>
      } />
      
      <Route path="/claims" element={
        <ProtectedRoute>
          <ClaimsList />
        </ProtectedRoute>
      } />
      
      <Route path="/claims/new" element={
        <ProtectedRoute>
          <NewClaim />
        </ProtectedRoute>
      } />
      
      <Route path="/claims/:id" element={
        <ProtectedRoute>
          <ClaimDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsManagement />
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersManagement />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
